import { isConsumer, isKiosk } from 'src/config'
import { h, resolveComponent } from 'vue'

/**
 * @typedef {import('vue-router').RouteRecordRaw} RouteRecordRaw
 */

/**
 * Builds the child route objects for a auth root parent route. Note the
 * `meta.layout` field can change which layout component is used for each route.
 * When `meta.layout` missing, it defaults to the parent route's layout (PlainLayout).
 *
 * We use a function to support dynamic generation and to ensure each parent route
 * has its own child routes that do not share references.
 * @returns {RouteRecordRaw[]}
 */
export function buildAuthChildRoutes (namespace = 'auth', rootPath = '/') {
  const accountLayout = (isConsumer || isKiosk) ? 'AccountLayout' : 'PlainLayout'

  // We define most of the following routes with paths starting with a leading slash ('/').
  // This causes the paths to be added as absolute paths at the top-level instead of being
  // nested under their parent route's path. We need to do this for finer control because
  // the parent route will have a path similar to '/auth' which we don't actually want to
  // use but is necessary to set a Layout component while avoiding conflicts with other
  // root path routes.
  const prependRoot = (path) => {
    path = _.trim(path, '/')
    return rootPath === '/' ? `/${path}` : `${rootPath}/${path}`
  }

  return [
    {
      name: `${namespace}:login`,
      path: prependRoot('login'),
      meta: { auth: { loggedOut: true } },
      component: () => import('src/modules/auth/pages/Login')
    },
    {
      name: `${namespace}:logout`,
      path: prependRoot('logout'),
      component: () => import('src/modules/auth/pages/Logout')
    },
    {
      name: `${namespace}:register`,
      path: prependRoot('register'),
      meta: { auth: { loggedOut: true } },
      component: () => import('src/modules/auth/pages/Register'),
      beforeEnter: (to, from, next) => {
        // Store the previous route information for the Register component
        if (from.name) {
          sessionStorage.setItem('registerFromRoute', JSON.stringify({
            name: from.name,
            path: from.path,
            fullPath: from.fullPath,
            params: from.params,
            query: from.query
          }))
        } else {
          // If no previous route (direct navigation), clear any stored route
          sessionStorage.removeItem('registerFromRoute')
        }
        next()
      }
    },
    {
      name: `${namespace}:password-root`,
      path: prependRoot('password'),
      component: { render: () => h(resolveComponent('router-view')) },
      children: [
        {
          name: `${namespace}:forgot-password`,
          path: 'forgot',
          component: () => import('src/modules/auth/pages/password/Forgot')
        },
        {
          name: `${namespace}:claim-account`,
          path: 'claim-account',
          component: () => import('src/modules/auth/pages/password/ClaimAccount')
        },
        {
          name: `${namespace}:reset-password`,
          path: 'reset/:token',
          component: () => import('src/modules/auth/pages/password/Reset')
        }
      ]
    },
    {
      name: `${namespace}:account`,
      path: prependRoot('account'),
      meta: {
        auth: true,
        layout: accountLayout
      },
      component: () => import('src/modules/auth/pages/Account')
    },
    {
      name: `${namespace}:time-card`,
      path: prependRoot('time-card'),
      meta: { auth: true },
      component: () => import('src/modules/auth/pages/TimeCard')
    },
    {
      name: `${namespace}:unauthorized`,
      path: prependRoot('401'),
      meta: { auth: true },
      component: () => import('src/pages/Error401')
    }
  ]
}
